import React from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Text, useStyleConfig} from '@chakra-ui/react'
import {Link} from 'react-router-dom'
import {calculateVerticalPadding} from '../utils'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {SortingTile} from './sorting-tile'
import {Navigation, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {ChevronRight, ChevronLeft} from '../../icons/arrows'
import SanitizedHtml from '../../html-sanitizer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {debounce} from 'lodash'

export const Sorting = ({
    variant = 'white',
    hasPaddings,
    heading = '',
    bodycopy = '',
    buttons = [],
    sortingGroup = [],
}) => {
    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('Sorting')
    const verticalPadding = calculateVerticalPadding('', hasPaddings)
    const categoryData = useGetCategoriesData()
    const {locale} = useMultiSite()
    const buttonsProps =
        buttons?.length === 0
            ? []
            : buttons?.map((b) =>
                  getGlobalLink(b.button, categoryData, locale?.alias || locale?.id),
              )

    const DEBOUNCE_WAIT = 200
    const debouncedSliderGroupResize = debounce((tileGroup) => {
        const tilesLinesHeight = []

        tileGroup.forEach((tile) => {
            let tilelinesHeight = {
                title: 0,
                bodycopy: 0,
            }

            Array.from(tile.getElementsByClassName('tile-title')).forEach((tileTitle) => {
                tileTitle.style.minHeight = null
                tilelinesHeight.title = tileTitle.offsetHeight
            })
            Array.from(tile.getElementsByClassName('tile-bodycopy')).forEach((tileBodycopy) => {
                tileBodycopy.style.minHeight = null
                tilelinesHeight.bodycopy = tileBodycopy.offsetHeight
            })

            tilesLinesHeight.push(tilelinesHeight)
        })

        const linesMax = {
            title: Math.max(...tilesLinesHeight.map((item) => item.title)),
            bodycopy: Math.max(...tilesLinesHeight.map((item) => item.bodycopy)),
        }

        tileGroup.forEach((tile) => {
            Array.from(tile.getElementsByClassName('tile-title')).forEach((tileTitle) => {
                tileTitle.style.minHeight = `${linesMax.title}px`
            })

            Array.from(tile.getElementsByClassName('tile-bodycopy')).forEach((tileBodycopy) => {
                tileBodycopy.style.minHeight = `${linesMax.bodycopy}px`
            })
        })
    }, DEBOUNCE_WAIT)

    return (
        <Flex
            __css={{...styles.main}}
            data-testid="sorting"
            py={verticalPadding}
            backgroundColor={variant === 'black' ? 'background.dark' : 'background.default'}
        >
            <Flex {...styles.container} flexDirection={'column'}>
                {heading && (
                    <Text
                        textStyle="head-large"
                        fontWeight={'medium'}
                        color={variant === 'black' ? 'text.inverse' : 'text.default'}
                        {...styles.heading}
                    >
                        {heading}
                    </Text>
                )}
                {bodycopy && (
                    <SanitizedHtml
                        as={Box}
                        html={bodycopy}
                        textStyle="body-large"
                        fontWeight={'normal'}
                        color={variant === 'black' ? 'text.inverse' : 'text.default'}
                        {...styles.bodycopy}
                    />
                )}
                {sortingGroup && sortingGroup.length > 0 && (
                    <Box __css={swiperStyles} {...styles.sortingSwiper}>
                        <Swiper
                            modules={[Navigation, Keyboard]}
                            navigation={{
                                prevEl: `.sorting-swiper-button-prev`,
                                nextEl: `.sorting-swiper-button-next`,
                            }}
                            keyboard={{
                                enabled: true,
                            }}
                            centerInsufficientSlides={true}
                            breakpoints={{
                                0: {
                                    slidesPerView: 1,
                                    enabled: sortingGroup.length > 1,
                                    spaceBetween: 0,
                                    loop: true,
                                },
                                768: {
                                    slidesPerView: 4,
                                    enabled: false,
                                    spaceBetween: 16,
                                    loop: false,
                                },
                                1024: {
                                    slidesPerView: 4,
                                    enabled: false,
                                    spaceBetween: 24,
                                    loop: false,
                                },
                            }}
                            onResize={(swiper) => {
                                debouncedSliderGroupResize(swiper?.slides)
                            }}
                        >
                            {sortingGroup.map((tile, tIndex) => (
                                <SwiperSlide key={`sorting-tile-${tIndex}`}>
                                    <SortingTile
                                        key={`sorting-tile-${tIndex}`}
                                        styles={styles}
                                        tile={tile}
                                        tIndex={tIndex}
                                        variant={variant}
                                    />
                                </SwiperSlide>
                            ))}
                            <ChevronLeft
                                className="sorting-swiper-button-prev"
                                aria-label="swiper button prev icon"
                                {...styles.chevronIcon}
                                left={0}
                            />
                            <ChevronRight
                                className="sorting-swiper-button-next"
                                aria-label="swiper button next icon"
                                {...styles.chevronIcon}
                                right={0}
                            />
                        </Swiper>
                    </Box>
                )}
                {buttonsProps.length > 0 && (
                    <Flex
                        flexDirection={{base: 'column', md: 'row'}}
                        {...styles.ctacontainer}
                        justifyContent={{base: 'flex-start', md: 'center'}}
                        alignItems={{base: 'center', md: 'flex-start'}}
                    >
                        {buttonsProps.map((b, bIndex) => {
                            const bVariant = bIndex === 0 ? 'primary' : 'secondary'
                            const bProps = b.isAbsolute
                                ? {
                                      as: 'a',
                                      href: b.url,
                                      target: '_blank',
                                      rel: 'noopener noreferrer',
                                  }
                                : {
                                      as: Link,
                                      to: b.url || '',
                                  }

                            return b.label && b.url ? (
                                <Button
                                    key={`sorting-button-${bIndex}`}
                                    {...bProps}
                                    {...styles.cta}
                                    variant={`${variant === 'black' ? 'inverse-' : ''}${bVariant}`}
                                >
                                    {b.label}
                                </Button>
                            ) : null
                        })}
                    </Flex>
                )}
            </Flex>
        </Flex>
    )
}

Sorting.propTypes = {
    hasPaddings: PropTypes.bool,
    variant: PropTypes.string,
    heading: PropTypes.string,
    bodycopy: PropTypes.string,
    sortingGroup: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            bodyCopy: PropTypes.string,
            image: PropTypes.shape(),
        }),
    ),
    buttons: PropTypes.arrayOf(PropTypes.shape()),
}
